import React, { useMemo } from 'react';
import {
  DynamicResultTable,
  FormattedDate,
  OpenSidebarPayload,
  RefundedLozenge,
  useSidebar,
  FormatCurrency,
} from '@css-xp/base';
import classNames from 'classnames';
import { Refund } from '@atlassian/cssxp-types-billing';
import {
  NEW_DATE_FORMAT,
  NEW_DATE_TIME_FORMAT,
  REFUND_PATH,
  REFUND_PATH_2,
  SourceSystem,
} from '@atlassian/cssxp-typescommon';
import { NO_DETAILS_TO_SHOW } from '@atlassian/cssxp-utils-entitlement';
import { GloballyAllowedSidebar } from '@atlassian/cssxp-utils-generic';
import { isInvoiceRefunded } from '@atlassian/cssxp-utils-invoice';
import { Link } from 'react-router-dom';
import './invoice-details-refund-results.scss';
import { InvoiceRefundStatusLozenge } from './invoice-refund-status-lozenge';

enum RefundTable {
  CREDIT_NUMBER = 'credit_note_number',
  STATUS = 'status',
  CREDIT_TYPE = 'type',
  ISSUED_DATE = 'issued_date',
  AMOUNT = 'amount',
  REQUESTED_BY = 'requested_by',
}

export interface Props {
  refunds: Refund[];
  source?: SourceSystem | null;
  /* The `refundsBaseRoute` prop is used in apps like PPC to correctly redirect
   * to its refunds details page which has different route structure than AC.
   * Eg.: /any-custom-route-for-refunds/
   * */
  refundsBaseRoute?: string;
  shouldDisableSidebar?: boolean;
  isDesignIteration1Enabled?: boolean;
}

const wrapInCell = (node?: React.ReactElement | string | null) => {
  return <div className="invoice-refund-cell">{node}</div>;
};

const headers = {
  [RefundTable.CREDIT_NUMBER]: {
    key: RefundTable.CREDIT_NUMBER,
    content: wrapInCell('Credit note number'),
  },
  [RefundTable.STATUS]: {
    key: RefundTable.STATUS,
    content: wrapInCell('Status'),
  },
  [RefundTable.CREDIT_TYPE]: {
    key: RefundTable.CREDIT_TYPE,
    content: wrapInCell('Type'),
  },
  [RefundTable.AMOUNT]: {
    key: RefundTable.AMOUNT,
    content: wrapInCell('Amount'),
  },
  [RefundTable.ISSUED_DATE]: {
    key: RefundTable.ISSUED_DATE,
    content: wrapInCell('Issued on'),
  },
  [RefundTable.REQUESTED_BY]: {
    key: RefundTable.REQUESTED_BY,
    content: wrapInCell('Requested By'),
  },
};

const makeHead = (source: SourceSystem | null | undefined, currency?: string, isDesignIteration1Enabled?: boolean) => {
  if (isDesignIteration1Enabled) {
    return {
      cells: [
        { ...headers[RefundTable.CREDIT_NUMBER], width: 17 },
        { ...headers[RefundTable.STATUS], width: 10 },
        ...(source === SourceSystem.CCP
          ? [{ ...headers[RefundTable.CREDIT_TYPE], width: 20, content: wrapInCell('Refund type') }]
          : []),
        { ...headers[RefundTable.ISSUED_DATE], width: 11, content: wrapInCell('Issued') },
        { ...headers[RefundTable.AMOUNT], width: 42, content: wrapInCell(`Amount${currency ? ` (${currency})` : ``}`) },
      ],
    };
  }

  return {
    cells: [
      headers[RefundTable.CREDIT_NUMBER],
      headers[RefundTable.STATUS],
      headers[RefundTable.ISSUED_DATE],
      ...(source === SourceSystem.CCP ? [headers[RefundTable.CREDIT_TYPE]] : []),
      headers[RefundTable.AMOUNT],
      headers[RefundTable.REQUESTED_BY],
    ],
  };
};

type SidebarArgType = {
  // eslint-disable-next-line no-unused-vars
  openSidebar: (arg: OpenSidebarPayload) => void;
};

const getColumnsCell = (refund: Refund, refundsBaseRoute?: string, isDesignIteration1Enabled?: boolean) => {
  const getRefundBaseUrl = (refund: Refund) => {
    if (refundsBaseRoute) {
      return refundsBaseRoute;
    }
    if (refund.sourceSystem?.toLowerCase() === SourceSystem.HAMS.toLowerCase()) {
      return `${REFUND_PATH}/`;
    }
    return `${REFUND_PATH_2}/`;
  };

  const columns = {
    [RefundTable.CREDIT_NUMBER]: {
      key: RefundTable.CREDIT_NUMBER,
      content: wrapInCell(
        <Link
          to={`${getRefundBaseUrl(refund)}${encodeURIComponent(refund.id)}`}
          className="invoice-link"
          onClick={(e) => e.stopPropagation()}
          data-testid={`${refund.id}-link`}
        >
          {refund.id}
        </Link>
      ),
    },
    [RefundTable.STATUS]: {
      key: RefundTable.STATUS,
      content: wrapInCell(
        isInvoiceRefunded(refund) ? <RefundedLozenge /> : <InvoiceRefundStatusLozenge status="PENDING" />
      ),
    },
    [RefundTable.ISSUED_DATE]: {
      content: wrapInCell(
        refund.createdDate && (
          <FormattedDate
            date={refund.createdDate}
            showTime={true}
            format={isDesignIteration1Enabled ? NEW_DATE_FORMAT : NEW_DATE_TIME_FORMAT}
          />
        )
      ),
    },
    [RefundTable.CREDIT_TYPE]: {
      key: RefundTable.CREDIT_TYPE,
      content: wrapInCell(refund.type),
    },
    [RefundTable.AMOUNT]: {
      key: RefundTable.AMOUNT,
      content: wrapInCell(
        refund.total ? (
          <FormatCurrency
            value={refund.total}
            currency={refund.currency}
            showCurrencyCode={!isDesignIteration1Enabled}
            showCommas
          />
        ) : (
          '-'
        )
      ),
    },
    [RefundTable.REQUESTED_BY]: {
      content: wrapInCell(refund.requestedBy ? <span className="email">{refund.requestedBy}</span> : '-'),
    },
  };

  if (isDesignIteration1Enabled) {
    return [
      columns[RefundTable.CREDIT_NUMBER],
      columns[RefundTable.STATUS],
      ...(refund.sourceSystem?.toLowerCase() === SourceSystem.CCP.toLowerCase()
        ? [columns[RefundTable.CREDIT_TYPE]]
        : []),
      columns[RefundTable.ISSUED_DATE],
      columns[RefundTable.AMOUNT],
    ];
  }
  return [
    columns[RefundTable.CREDIT_NUMBER],
    columns[RefundTable.STATUS],
    columns[RefundTable.ISSUED_DATE],
    ...(refund.sourceSystem?.toLowerCase() === SourceSystem.CCP.toLowerCase()
      ? [columns[RefundTable.CREDIT_TYPE]]
      : []),
    columns[RefundTable.AMOUNT],
    columns[RefundTable.REQUESTED_BY],
  ];
};

const makeColumns = ({
  refunds,
  sidebar,
  refundsBaseRoute,
  shouldDisableSidebar,
  isDesignIteration1Enabled,
}: {
  refunds: Refund[];
  sidebar: SidebarArgType;
  refundsBaseRoute?: string;
  shouldDisableSidebar?: boolean;
  isDesignIteration1Enabled?: boolean;
}) => {
  const sidebarType = GloballyAllowedSidebar.REFUND_PREVIEW;

  return refunds?.map((refund: Refund, index) => {
    const rowKey = `refund-row-${index}`;
    const { openSidebar } = sidebar;
    const payload = {
      refundId: refund.id,
    };

    return {
      key: rowKey,
      cells: getColumnsCell(refund, refundsBaseRoute, isDesignIteration1Enabled),
      onClick: () => {
        !shouldDisableSidebar &&
          openSidebar({
            sidebarType,
            payload,
            sidebarOptions: {
              sidebarWidth: '600px',
              isOverlay: true,
            },
          });
      },
    };
  });
};

const emptyStateProps = {
  header: '',
  description: NO_DETAILS_TO_SHOW,
};

const RefundResults = (props: Props) => {
  const sidebar = useSidebar();
  const { refunds, source, refundsBaseRoute, shouldDisableSidebar = false, isDesignIteration1Enabled } = props;

  const currency = useMemo(() => {
    if (refunds?.length > 0) {
      return refunds[0].currency;
    }
    return '';
  }, [refunds]);

  return (
    <div
      className={classNames('invoice-refunds-v2-container', {
        'invoice-refunds-table-container': isDesignIteration1Enabled,
      })}
    >
      <DynamicResultTable
        header={<h4 className="heading">Refunds</h4>}
        head={makeHead(source, currency, isDesignIteration1Enabled)}
        rows={makeColumns({ refunds, sidebar, refundsBaseRoute, shouldDisableSidebar, isDesignIteration1Enabled })}
        loadingSpinnerSize="small"
        emptyStateProps={emptyStateProps}
      />
    </div>
  );
};

export default RefundResults;
